// @flow
// @flow-runtime warn
import React, { useState, useMemo, type Node, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import clone from 'ramda/es/clone';
import path from 'ramda/es/path';
import { useTranslation } from 'react-i18next';

import api from 'app/actions/api';
import action from 'app/common/lib/action';

import SubsidiesContext, { defaultSubsidies } from './SubsidiesContext';
import type { Subsidies } from './SubsidiesTypes';


type Props = {
  timezone?: string,
  children: Node,
  personId: number,
  from: string,
  to: string,
  schoolId?: string,
}

const SubsidiesData = ({
  timezone = 'Europe/Moscow',
  children,
  personId,
  from,
  to,
  schoolId,
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('cafeteria');
  const [subsidies, setSubsidies] = useState(defaultSubsidies);
  const [loadingSubsidies, setLoadingSubsidies] = useState(true);

  const subsidiesRequest = useCallback(async () => {
    const requestPath = 'cafeteria/customer/subsidies';
    const type = 'get';
    const rest = {
      params: {
        customer_id: personId,
        from_date: from,
        to_date: to,
        school_id: schoolId,
        for_camp: !!schoolId,
      },
    };
    try {
      setLoadingSubsidies(true);
      const res = await api(requestPath, type, rest);
      const subsidiesResult: Subsidies = path(['data', 'data'], res) || defaultSubsidies;
      setSubsidies(subsidiesResult);
      setLoadingSubsidies(false);
    } catch (error) {
      const errorSave = {
        err: t('subsidies_fetch_error', 'Не удалось получить дотации'),
        path: requestPath,
        rest,
        type,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
    }
  }, [dispatch, from, to, personId, t, schoolId]);

  useEffect(() => {
    subsidiesRequest();
  }, [subsidiesRequest]);

  const contextValue = useMemo(() => ({
    content: {
      timezone,
      subsidies,
    },
    loading: {
      subsidies: loadingSubsidies,
    },
    request: {
      subsidies: subsidiesRequest,
    },
  }), [
    timezone,
    subsidies,
    loadingSubsidies,
    subsidiesRequest,
  ]);

  return (
    <SubsidiesContext.Provider value={contextValue}>
      {children}
    </SubsidiesContext.Provider>
  );
};

export default SubsidiesData;
